import {
  Grid,
  Card,
  CardHeader,
  CardContent,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import TeacherManagementService from "../../services/TeacherManagementService";
import { capitalize } from "../../utils/helpers";
import CustomLoader from "../../components/CustomLoader";
import errorHandler from "../../utils/errorHandler";
import React, { useEffect, useState } from "react";
import { fDateTime } from "../../utils/formatTime";
import CardLayout from "../../layouts/CardLayout";
import { useParams } from "react-router-dom";
import { saveAs } from "file-saver";

const ViewReportPerTaskForStudent = () => {
  const { studentId, courseId, taskId, studentUniqueId } = useParams();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [maxOptionsCount, setMaxOptionsCount] = useState(0);
  const [downloadData, setDownloadData] = useState([]);
  const [selectedAttemptIndex, setSelectedAttemptIndex] = useState("live");

  useEffect(() => {
    const fetchData = async () => {
      const payload = { studentId, courseId, taskId };
      try {
        setLoading(true);
        const response = await TeacherManagementService.liviewReportPerTask(payload);

        const fetchedData = response?.data?.data?.result || [];
        const downloadFatchData = response?.data?.data?.taskAttempts || [];

        let maxOptions = 0;
        fetchedData.forEach(item => {
          item?.questionData?.questions?.forEach(q => {
            if (q.options?.length > maxOptions) {
              maxOptions = q.options.length;
            }
          });
        });
        downloadFatchData.forEach(attempt => {
          (attempt.answers || []).forEach(answer => {
            answer?.questionData?.questions?.forEach(q => {
              if (q.options?.length > maxOptions) {
                maxOptions = q.options.length;
              }
            });
          });
        });

        setData(fetchedData);
        setDownloadData(downloadFatchData);
        setMaxOptionsCount(maxOptions);
        setLoading(false);
      } catch (error) {
        errorHandler(error);
        setLoading(false);
      }
    };
    fetchData();
  }, [studentId, courseId, taskId]);

  const downloadCSV = () => {
    if (downloadData.length === 0) {
      alert("No data to download.");
      return;
    }

    const optionHeaders = Array.from({ length: maxOptionsCount }, (_, i) => `Option ${i + 1}`);
    const headers = [
      "No.",
      "Audio",
      ...optionHeaders,
      "Correct Answer",
      "Your Answer",
      "Attempt",
      "Response Duration",
      "Response Date & Time",
    ];

    const csvRows = [];

    downloadData.forEach((attempt, attemptIndex) => {
      csvRows.push(`Attempt: ${attempt.attempts}`);
      csvRows.push("");
      csvRows.push(headers.join(","));

      let questionCounter = 1;

      (attempt.answers || []).forEach((answer) => {
        (answer.questionData?.questions || []).forEach((question) => {
          const row = [
            questionCounter++,
            question.audio || "",
          ];

          for (let i = 0; i < maxOptionsCount; i++) {
            row.push(capitalize(question.options?.[i] || ""));
          }

          const correctAnswerText = question.options?.[parseInt(question.correctAnswer)] || "";
          row.push(capitalize(correctAnswerText));

          const yourAnswer = answer.noResponse
            ? "Time out"
            : `${capitalize(answer.answer)}  ${answer.noResponse ? "Time out" : answer.correct ? "(Correct)" : "(Incorrect)"}`;
          row.push(yourAnswer);

          row.push(`Attempt: ${attempt.attempts}`);
          row.push(`${answer.responseTime} Milliseconds`);
          row.push(fDateTime(answer.updatedAt));

          csvRows.push(row.join(","));
        });
      });

      csvRows.push("");
    });

    const BOM = "\uFEFF";
    const blob = new Blob([BOM + csvRows.join("\n")], { type: "text/csv;charset=utf-8;" });
    saveAs(blob, `Task_Report_${studentUniqueId}.csv`);
  };

  let tableDataToShow = [];
  if (selectedAttemptIndex === "live") {
    if (downloadData.length > 0) {
      // show last attempt instead of actual live
      tableDataToShow = downloadData[downloadData.length - 1].answers || [];
    } else {
      tableDataToShow = [];
    }
  } else if (downloadData[selectedAttemptIndex]) {
    tableDataToShow = downloadData[selectedAttemptIndex].answers || [];
  }


  return (
    <Box>
      <CardLayout>
        <CardHeader
          titleTypographyProps={{ variant: "h4" }}
          title="Student Live Report Per Task"
        />
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "right",
            mb: 2,
          }}
        >
          <FormControl>
            <InputLabel id="attempt-select-label">View</InputLabel>
            <Select
              labelId="attempt-select-label"
              label="View"
              value={selectedAttemptIndex}
              onChange={(e) => setSelectedAttemptIndex(e.target.value)}
            >
              <MenuItem value="live">Live (Current)</MenuItem>
              {downloadData.map((attempt, index) => (
                <MenuItem key={index} value={index}>
                  Attempt {attempt.attempts || index + 1}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button variant="contained" color="primary" onClick={downloadCSV} style={{ padding: '12px', marginLeft: "21px" }}>
            Download CSV
          </Button>
        </Box>

        <CardContent>
          {loading ? (
            <Box mt={2} sx={{ display: "flex", justifyContent: "center" }}>
              <CustomLoader />
            </Box>
          ) : (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Card sx={{ boxShadow: 4, borderRadius: 3 }}>
                  <CardContent>
                    {tableDataToShow.length > 0 ? (
                      <TableContainer component={Paper}>
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell>No.</TableCell>
                              <TableCell>Audio</TableCell>
                              {Array.from({ length: maxOptionsCount }, (_, i) => (
                                <TableCell key={i}>{`Option ${i + 1}`}</TableCell>
                              ))}
                              <TableCell>Correct Answer</TableCell>
                              <TableCell>Your Answer</TableCell>
                              <TableCell>Attempt</TableCell>
                              <TableCell>Response Duration</TableCell>
                              <TableCell>Response Date & Time</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {selectedAttemptIndex === "live" ? (
                              tableDataToShow.map((item, itemIndex) =>
                                item?.questionData?.questions?.map((question, qIndex) => (
                                  <TableRow key={`${itemIndex}-${qIndex}`}>
                                    <TableCell>{itemIndex + 1}</TableCell>
                                    <TableCell>{question.audio}</TableCell>
                                    {Array.from({ length: maxOptionsCount }, (_, i) => (
                                      <TableCell key={i}>
                                        {capitalize(question.options?.[i] || "")}
                                      </TableCell>
                                    ))}
                                    <TableCell>
                                      {capitalize(
                                        question.options?.[parseInt(question.correctAnswer)] || ""
                                      )}
                                    </TableCell>
                                    <TableCell>
                                      {item.noResponse
                                        ? "Time out"
                                        : `${capitalize(item.answer)} ${item.correct ? "(Correct)" : "(Incorrect)"
                                        }`}
                                    </TableCell>
                                    <TableCell>{item.attempts}</TableCell>
                                    <TableCell>{item.responseTime} Milliseconds</TableCell>
                                    <TableCell>{fDateTime(item.updatedAt)}</TableCell>
                                  </TableRow>
                                ))
                              )
                            ) : (
                              tableDataToShow.map((answer, answerIndex) =>
                                answer?.questionData?.questions?.map((question, qIndex) => (
                                  <TableRow key={`${answerIndex}-${qIndex}`}>
                                    <TableCell>{answerIndex + 1}</TableCell>
                                    <TableCell>{question.audio}</TableCell>
                                    {Array.from({ length: maxOptionsCount }, (_, i) => (
                                      <TableCell key={i}>
                                        {capitalize(question.options?.[i] || "")}
                                      </TableCell>
                                    ))}
                                    <TableCell>
                                      {capitalize(
                                        question.options?.[parseInt(question.correctAnswer)] || ""
                                      )}
                                    </TableCell>
                                    <TableCell>
                                      {answer.noResponse
                                        ? "Time out"
                                        : `${capitalize(answer.answer)} ${answer.correct ? "(Correct)" : "(Incorrect)"
                                        }`}
                                    </TableCell>
                                    <TableCell>
                                      {downloadData[selectedAttemptIndex]?.attempts}
                                    </TableCell>
                                    <TableCell>{answer.responseTime} Milliseconds</TableCell>
                                    <TableCell>{fDateTime(answer.updatedAt)}</TableCell>
                                  </TableRow>
                                ))
                              )
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <div>No grades available</div>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}
        </CardContent>
      </CardLayout>
    </Box>
  );
};

export default ViewReportPerTaskForStudent;